import { useEffect, useMemo } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { fetchCustomerDetail } from '#/api/crmMgmt/customer/fetchDetail';
import {
  fetchCustomerSelectOptions,
  mergeCustomerSelectOption,
} from '#/api/crmMgmt/customer/fetchSelectOptions';
import { createQuotation } from '#/api/crmMgmt/quotation/create';
import { fetchQuotationDetail } from '#/api/crmMgmt/quotation/fetchDetail';
import { updateQuotation } from '#/api/crmMgmt/quotation/update';
import { fetchPaymentTermSelectOptions } from '#/api/system/paymentTerm';
import { ROUTES } from '#/config/routes';
import useCreateEditSave from '#/hooks/useCreateEditSave';
import useFormValidationSummary from '#/hooks/useFormValidationSummary';
import { cn } from '#/lib/utils';
import { Button } from '#/modules/commonComponents/button';
import FloatingInput from '#/modules/commonComponents/form/FloatingInput';
import FloatingTextarea from '#/modules/commonComponents/form/FloatingTextarea';
import FloatingSelect from '#/modules/commonComponents/form/select';
import FormValidationSummary from '#/modules/commonComponents/form/FormValidationSummary';
import QuotationLinesEditor from '#/modules/crmMgmt/quotation/components/QuotationLinesEditor';
import QuotationStatusBadge from '#/modules/crmMgmt/quotation/components/QuotationStatusBadge';
import {
  getQuotationApiErrorMessage,
  isQuotationVersionConflict,
} from '#/modules/crmMgmt/quotation/helper/apiError';
import {
  buildQuotationCreateBody,
  buildQuotationPatchBody,
  mapQuotationDetailToFormValues,
  pickQuotationContactSnapshot,
} from '#/modules/crmMgmt/quotation/helper/quotationLines';
import { canEditQuotationDraft } from '#/modules/crmMgmt/quotation/helper/quotationStatus';
import {
  QUOTATION_FIELD_LABELS,
  quotationFormSchema,
  resolveQuotationFieldLabel,
} from '#/modules/crmMgmt/quotation/schemas/quotationSchema';
import PageShell from '#/modules/infra/layout/PageShell';
import { ROUNDING_MODE_OPTIONS } from '#/utils/roundingMode';
import {
  customerDetailQueryKey,
  lookupQueryKey,
  quotationDetailQueryKey,
  quotationListQueryKeyPrefix,
} from '#/utils/queryKeys';
import { toast } from '#/utils/toast';

const DEFAULT_VALUES = {
  customerId: '',
  contactName: '',
  contactPhone: '',
  customerAddress: '',
  paymentTermId: '',
  roundingMode: 'HALF_UP',
  remark: '',
  lines: [],
};

function ensureOption(options, value, label) {
  if (!value || options.some((option) => option.value === value)) {
    return options;
  }
  return [...options, { value, label: label || String(value) }];
}

const CreateEditQuotationPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const {
    isSubmitAttempted,
    markSubmitAttempted,
    resetValidationSummary,
    customMessages,
  } = useFormValidationSummary();

  const detailQuery = useQuery({
    queryKey: quotationDetailQueryKey(id),
    queryFn: () => fetchQuotationDetail(id),
    enabled: Boolean(id),
  });
  const detail = detailQuery.data?.data;

  const customerOptionsQuery = useQuery({
    queryKey: lookupQueryKey('customerSelect'),
    queryFn: fetchCustomerSelectOptions,
  });

  const paymentTermOptionsQuery = useQuery({
    queryKey: lookupQueryKey('paymentTermSelect'),
    queryFn: fetchPaymentTermSelectOptions,
  });

  async function saveQuotation(values) {
    if (!id) {
      return createQuotation(buildQuotationCreateBody(values));
    }
    return updateQuotation(id, buildQuotationPatchBody(values, detail?.record?.updatedAt));
  }

  const { isEdit, isPending, save } = useCreateEditSave({
    id,
    mutationFn: saveQuotation,
    listQueryKeyPrefix: quotationListQueryKeyPrefix,
    detailQueryKey: quotationDetailQueryKey,
    getDetailPath: ROUTES.quotationDetail,
    afterUpdateNavigateTo: id ? ROUTES.quotationDetail(id) : null,
    getErrorMessage: (error) => getQuotationApiErrorMessage(error, '儲存失敗'),
    isVersionConflict: isQuotationVersionConflict,
    onVersionConflict: () => detailQuery.refetch(),
  });

  const {
    register,
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(quotationFormSchema),
    defaultValues: DEFAULT_VALUES,
  });

  useEffect(() => {
    if (!isEdit || !detail) {
      return;
    }
    if (!canEditQuotationDraft(detail.status)) {
      toast.warning('只有草稿可以編輯，已返回詳情');
      navigate(ROUTES.quotationDetail(id), { replace: true });
    }
  }, [isEdit, detail, id, navigate]);

  useEffect(() => {
    if (!detail) {
      return;
    }
    resetValidationSummary();
    reset(mapQuotationDetailToFormValues(detail));
  }, [detail, reset, resetValidationSummary]);

  const customerOptions = useMemo(() => {
    const options = mergeCustomerSelectOption(
      customerOptionsQuery.data ?? [],
      detail?.customer,
    );
    return [{ value: '', label: '請選擇' }, ...options];
  }, [customerOptionsQuery.data, detail?.customer]);

  const paymentTermOptions = useMemo(() => {
    const options = ensureOption(
      paymentTermOptionsQuery.data ?? [],
      detail?.paymentTermId,
      detail?.paymentTermName,
    );
    return [{ value: '', label: '不指定' }, ...options];
  }, [paymentTermOptionsQuery.data, detail?.paymentTermId, detail?.paymentTermName]);

  // 換客戶時以主檔預設聯絡人／帳單地址覆寫文字快照（後端要求同一 PATCH 送新快照）
  async function handleCustomerChange(customerId) {
    if (!customerId) {
      return;
    }
    try {
      const result = await queryClient.fetchQuery({
        queryKey: customerDetailQueryKey(customerId),
        queryFn: () => fetchCustomerDetail(customerId),
      });
      const snapshot = pickQuotationContactSnapshot(result?.data);
      Object.entries(snapshot).forEach(([key, value]) => {
        setValue(key, value, { shouldDirty: true });
      });
    } catch {
      toast.warning('讀取客戶聯絡資料失敗，請手動填寫聯絡人與地址');
    }
  }

  function onSubmit(values) {
    markSubmitAttempted();
    save(values);
  }

  const pageTitle = isEdit ? '編輯報價單' : '新增報價單';
  const isPageLoading = isEdit && detailQuery.isLoading;
  const isDetailNotFound = isEdit && detailQuery.isError;
  const isReadOnlyEdit = isEdit && detail && !canEditQuotationDraft(detail.status);
  const backTo = isEdit ? ROUTES.quotationDetail(id) : ROUTES.quotation;

  return (
    <PageShell variant="form">
      <div className="layout-shell__body">
        <div className="w-full">
          <div className="create_edit_page__header">
            <div className="flex items-center gap-2">
              <h1 className="page_title mb-0! flex flex-wrap items-center gap-2">
                <span className="border-l-5 border-primary-400 pl-2.5">{pageTitle}</span>
                <QuotationStatusBadge status={isEdit ? (detail?.status || 'DRAFT') : 'DRAFT'} size="sm" />
                {detail?.quotationNo ? (
                  <span className="text-base font-normal text-gray-600">{detail.quotationNo}</span>
                ) : null}
              </h1>
              <Button type="button" variant="outline" asChild>
                <Link to={backTo}>{isEdit ? '返回詳情' : '返回列表'}</Link>
              </Button>
            </div>
          </div>

          {isPageLoading ? <p className="text-sm text-slate-500">載入中…</p> : null}

          {isDetailNotFound ? (
            <p className="text-sm text-destructive">
              {getQuotationApiErrorMessage(detailQuery.error, '找不到報價單')}
            </p>
          ) : null}

          {!isPageLoading && !isDetailNotFound && !isReadOnlyEdit ? (
            <div className="space-y-4">
              <FormValidationSummary
                show={isSubmitAttempted}
                errors={errors}
                fieldLabels={QUOTATION_FIELD_LABELS}
                fieldLabelResolver={resolveQuotationFieldLabel}
                customMessages={customMessages}
              />

              <form
                className="sheet create_edit create_edit_form create_edit_form--page-scroll"
                onSubmit={handleSubmit(onSubmit, markSubmitAttempted)}
                noValidate
              >
                <div className="create_edit_form__body divide-y divide-gray-500 pb-0!">
                  <section className="pb-6">
                    <div className="form-block">
                      <h2 className="form-block__title">報價資訊</h2>
                    </div>
                    <div className={cn('grid grid-cols-1 gap-3', '@4xl/sheet:grid-cols-3')}>
                      <FloatingSelect
                        id="customerId"
                        label="客戶"
                        options={customerOptions}
                        control={control}
                        errors={errors}
                        isDisabled={customerOptionsQuery.isLoading}
                        noOptionsMessage="無啟用中的客戶"
                        onChange={handleCustomerChange}
                      />
                      <FloatingInput
                        id="contactName"
                        label="聯絡人（選填）"
                        register={register}
                        errors={errors}
                      />
                      <FloatingInput
                        id="contactPhone"
                        label="聯絡電話（選填）"
                        register={register}
                        errors={errors}
                      />
                      <div className="@4xl/sheet:col-span-3">
                        <FloatingInput
                          id="customerAddress"
                          label="客戶地址（選填）"
                          register={register}
                          errors={errors}
                        />
                      </div>
                      <FloatingSelect
                        id="paymentTermId"
                        label="付款條件（選填）"
                        options={paymentTermOptions}
                        control={control}
                        errors={errors}
                        isDisabled={paymentTermOptionsQuery.isLoading}
                        noOptionsMessage="無啟用中的付款條件"
                      />
                      <FloatingSelect
                        id="roundingMode"
                        label="總額取整"
                        options={ROUNDING_MODE_OPTIONS}
                        control={control}
                        errors={errors}
                        isClearable={false}
                      />
                      <div className="@4xl/sheet:col-span-3">
                        <FloatingTextarea
                          id="remark"
                          label="內部備註"
                          register={register}
                          errors={errors}
                        />
                      </div>
                    </div>
                    <p className="mt-2 text-xs text-muted-foreground">
                      選客戶時會帶入主檔預設聯絡人與帳單地址，可再修改；報價單只保存文字快照，不回寫客戶主檔。
                    </p>
                  </section>

                  <QuotationLinesEditor
                    control={control}
                    register={register}
                    errors={errors}
                  />
                </div>

                <div className="create_edit_form__actions mt-4 flex flex-wrap justify-end gap-2">
                  <Button type="button" variant="outline" asChild>
                    <Link to={backTo}>取消</Link>
                  </Button>
                  <Button type="submit" disabled={isPending}>
                    {isPending ? '儲存中…' : (isEdit ? '儲存草稿' : '建立草稿')}
                  </Button>
                </div>
              </form>
            </div>
          ) : null}
        </div>
      </div>
    </PageShell>
  );
};

export default CreateEditQuotationPage;
